import React from 'react';

import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import AgentHubSelect from './AgentHubSelect';
import {
  type AgentHubFilters,
  CADENCES,
  CHART_METRICS,
  FILTER_DEFINITIONS,
  type FilterKey,
  SIGNUP_LEVELS,
  SPLITS,
  defaultFilters,
  lastCompleteDay,
} from './model';

type Props = {
  filters: AgentHubFilters;
  options: Record<string, Array<string>>;
  onChange: (change: Partial<AgentHubFilters>) => void;
};

/** Option lists stop at 500 values, so longer lists also accept a typed value. */
const OPTION_LIMIT = 500;

export default function AgentHubControls({ filters, options, onChange }: Props) {
  const select = (key: FilterKey, values: Array<string>) =>
    onChange({ selections: { ...filters.selections, [key]: values } });
  const fields = FILTER_DEFINITIONS.filter(({ agents }) => filters.tab !== 'agents' || agents);
  return (
    <Paper variant="outlined" sx={{ p: 2, position: { lg: 'sticky' }, top: { lg: 16 } }}>
      <Stack gap={1.5}>
        <Typography variant="subtitle2">Dates</Typography>
        <Stack direction="row" gap={1}>
          <TextField
            label="Start date"
            type="date"
            size="small"
            value={filters.from}
            onChange={(event) => onChange({ from: event.target.value })}
            inputProps={{ max: lastCompleteDay() }}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <TextField
            label="End date"
            type="date"
            size="small"
            value={filters.to}
            onChange={(event) => onChange({ to: event.target.value })}
            inputProps={{ max: lastCompleteDay() }}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
        </Stack>
        {filters.tab !== 'agents' && (
          <ToggleButtonGroup
            size="small"
            exclusive
            fullWidth
            value={filters.refunds}
            onChange={(_, value) => value && onChange({ refunds: value })}
            aria-label="Before or after refunds"
          >
            <ToggleButton value="BR">Before refunds (BR)</ToggleButton>
            <ToggleButton value="AR">After refunds (AR)</ToggleButton>
          </ToggleButtonGroup>
        )}
        {filters.tab !== 'finance' && (
          <AgentHubSelect
            label="Date cadence (chart)"
            value={filters.cadence}
            choices={CADENCES}
            onChange={(cadence) => onChange({ cadence })}
          />
        )}
        {filters.tab === 'orders' && (
          <>
            <AgentHubSelect
              label="Metric"
              value={filters.chartMetric}
              choices={CHART_METRICS}
              onChange={(chartMetric) => onChange({ chartMetric })}
            />
            <AgentHubSelect
              label="Data split"
              value={filters.split}
              choices={SPLITS}
              onChange={(split) => onChange({ split })}
            />
            {filters.split === 'agent_path' && (
              <AgentHubSelect
                label="Split levels"
                value={filters.depth}
                choices={[
                  { value: 1, label: 'Affiliation' },
                  { value: 2, label: 'Affiliation › Agency' },
                  { value: 3, label: 'Affiliation › Agency › Agent' },
                ]}
                onChange={(depth) => onChange({ depth: depth })}
              />
            )}
          </>
        )}
        {filters.tab === 'agents' && (
          <>
            <AgentHubSelect
              label="Sign-up metric"
              value={filters.signupLevel}
              choices={SIGNUP_LEVELS}
              onChange={(signupLevel) => onChange({ signupLevel })}
            />
            {(
              [
                ['showAffiliation', 'Show affiliation?'],
                ['showAgencies', 'Show agencies?'],
                ['showAgents', 'Show agents?'],
              ] as const
            ).map(([key, label]) => (
              <FormControlLabel
                key={key}
                control={
                  <Switch
                    size="small"
                    checked={filters[key]}
                    onChange={(event) => onChange({ [key]: event.target.checked })}
                  />
                }
                label={label}
              />
            ))}
          </>
        )}
        <Typography variant="subtitle2" sx={{ pt: 1 }}>
          Filters
        </Typography>
        {fields.map(({ key, label }) => {
          const selected = filters.selections[key];
          const listed = options[key] ?? [];
          return (
            <Autocomplete
              key={key}
              multiple
              freeSolo={listed.length >= OPTION_LIMIT}
              disableCloseOnSelect
              size="small"
              limitTags={1}
              options={[...new Set([...listed, ...selected])]}
              value={selected}
              onChange={(_, values) => select(key, values.map(String))}
              renderTags={(values, getTagProps) =>
                values.map((option, index) => {
                  const { key: tagKey, ...tagProps } = getTagProps({ index });
                  return <Chip key={tagKey} {...tagProps} label={option} size="small" />;
                })
              }
              renderOption={(props, option, { selected: checked }) => (
                <Box component="li" {...props} key={option}>
                  <Checkbox checked={checked} size="small" sx={{ mr: 0.5 }} />
                  {option}
                </Box>
              )}
              renderInput={(params) => (
                <TextField {...params} label={label} placeholder={selected.length ? '' : 'All'} />
              )}
            />
          );
        })}
        <Button onClick={() => onChange(defaultFilters(filters.tab))}>Reset</Button>
      </Stack>
    </Paper>
  );
}
